import {
    CRUDStorage,
    LocalStorageAdapter,
    FileMenu,
    Modal,
    LoadModal,
    PromptModal,
    loadFromFile,
    exportToFile
} from './lib/crud-storage/index.js';
import mermaid from 'mermaid';
import { renderMermaidAscii } from 'beautiful-mermaid';

// ============ PlantUML Encoding ============
function encode64(data) {
    let r = "";
    for (let i = 0; i < data.length; i += 3) {
        if (i + 2 === data.length) {
            r += append3bytes(data[i], data[i + 1], 0);
        } else if (i + 1 === data.length) {
            r += append3bytes(data[i], 0, 0);
        } else {
            r += append3bytes(data[i], data[i + 1], data[i + 2]);
        }
    }
    return r;
}

function append3bytes(b1, b2, b3) {
    const c1 = b1 >> 2;
    const c2 = ((b1 & 0x3) << 4) | (b2 >> 4);
    const c3 = ((b2 & 0xF) << 2) | (b3 >> 6);
    const c4 = b3 & 0x3F;
    return encode6bit(c1 & 0x3F) + encode6bit(c2 & 0x3F) + encode6bit(c3 & 0x3F) + encode6bit(c4 & 0x3F);
}

function encode6bit(b) {
    if (b < 10) return String.fromCharCode(48 + b);
    b -= 10;
    if (b < 26) return String.fromCharCode(65 + b);
    b -= 26;
    if (b < 26) return String.fromCharCode(97 + b);
    b -= 26;
    return b === 0 ? '-' : '_';
}

function plantumlEncode(text) {
    const compressed = pako.deflate(new TextEncoder().encode(text), { level: 9 });
    return '~1' + encode64(compressed);
}

// ============ DOM Elements ============
const umlInput = document.getElementById('umlInput');
const preview = document.getElementById('preview');
const previewWrapper = document.getElementById('previewWrapper');
const formatSelect = document.getElementById('format');
const templateSelect = document.getElementById('template');
const diagramTypeSelect = document.getElementById('diagramType');
const lineNumbers = document.getElementById('lineNumbers');
const statusDot = document.getElementById('statusDot');
const statusText = document.getElementById('statusText');
const zoomLevel = document.getElementById('zoomLevel');
const editorPanel = document.getElementById('editorPanel');
const resizer = document.getElementById('resizer');
const fileMenuContainer = document.getElementById('fileMenuContainer');
const diagramInfo = document.getElementById('diagramInfo');
const storageCount = document.getElementById('storageCount');

mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    securityLevel: 'loose'
});

const DEFAULT_DIAGRAM_TYPE = 'plantuml';
const DIAGRAM_TYPES = ['plantuml', 'mermaid'];
const DIAGRAM_HEADER_REGEX = /^<udiagram\s+type="(plantuml|mermaid)"\s*><\/diagram>\s*/i;

// ============ State ============
let currentZoom = 100;
let renderTimeout = null;
let isFullscreen = false;
let currentUrl = '';
const diagramStorage = new CRUDStorage(
    new LocalStorageAdapter('diagrammer-diagrams'),
    { timestamps: true }
);
let currentDiagram = null;
let lastMermaidSVG = '';
let lastMermaidText = '';
let mermaidRenderId = 0;

// ============ Templates ============
const templatePresets = {
    plantuml: {
        sequence: `@startuml
Alice -> Bob: Authentication Request
Bob --> Alice: Authentication Response
Alice -> Bob: Another request
Alice <-- Bob: Another response
@enduml`,
        usecase: `@startuml
left to right direction
actor Guest as g
actor User as u
package "Web Application" {
    usecase "View Items" as UC1
    usecase "Make Purchase" as UC2
    usecase "View Profile" as UC3
}
g --> UC1
u --> UC1
u --> UC2
u --> UC3
@enduml`,
        class: `@startuml
class Animal {
    +name: String
    +age: int
    +makeSound(): void
}

class Dog extends Animal {
    +breed: String
    +fetch(): void
}

class Cat extends Animal {
    +indoor: boolean
    +meow(): void
}

Animal <|-- Dog
Animal <|-- Cat
@enduml`,
        activity: `@startuml
start
:Initialize;
if (Valid input?) then (yes)
    :Process data;
    :Generate output;
else (no)
    :Show error;
endif
:Cleanup;
stop
@enduml`,
        state: `@startuml
[*] --> Idle
Idle --> Processing : start
Processing --> Success : done
Processing --> Error : fail
Success --> Idle : reset
Error --> Idle : reset
Success --> [*]
@enduml`,
        component: `@startuml
package "Frontend" {
    [Web App]
    [Mobile App]
}

package "Backend" {
    [API Gateway]
    [Auth Service]
    [User Service]
}

database "PostgreSQL" as db

[Web App] --> [API Gateway]
[Mobile App] --> [API Gateway]
[API Gateway] --> [Auth Service]
[API Gateway] --> [User Service]
[User Service] --> db
@enduml`,
        er: `@startuml
entity User {
    * id : int <<PK>>
    --
    * email : varchar
    * password : varchar
    created_at : timestamp
}

entity Post {
    * id : int <<PK>>
    --
    * user_id : int <<FK>>
    * title : varchar
    content : text
}

entity Comment {
    * id : int <<PK>>
    --
    * post_id : int <<FK>>
    * user_id : int <<FK>>
    body : text
}

User ||--o{ Post
User ||--o{ Comment
Post ||--o{ Comment
@enduml`,
        mindmap: `@startmindmap
* Project
** Planning
*** Requirements
*** Timeline
*** Budget
** Development
*** Frontend
*** Backend
*** Testing
** Deployment
*** Staging
*** Production
@endmindmap`,
        gantt: `@startgantt
Project starts 2024-01-01
[Design] lasts 10 days
[Development] lasts 20 days
[Development] starts at [Design]'s end
[Testing] lasts 10 days
[Testing] starts at [Development]'s end
[Deployment] lasts 5 days
[Deployment] starts at [Testing]'s end
@endgantt`
    },
    mermaid: {
        sequence: `sequenceDiagram
    participant User
    participant App
    participant Server
    User->>App: Request data
    App->>Server: Fetch
    Server-->>App: Response
    App-->>User: Render data`,
        flowchart: `flowchart TD
    A[Start] --> B{Is input valid?}
    B -- Yes --> C[Process data]
    B -- No --> D[Show error]
    C --> E[Generate output]
    E --> F[End]`,
        class: `classDiagram
    class User {
        +id: number
        +name: string
        +login()
    }
    class Session {
        +token: string
        +expiresAt: Date
        +validate()
    }
    User --> Session`,
        gantt: `gantt
    title Software Project Timeline
    dateFormat  YYYY-MM-DD
    section Planning
    Requirements   :done,    des1, 2024-01-01, 5d
    section Development
    Frontend       :active,  des2, 2024-01-08, 10d
    Backend        :         des3, after des2, 12d
    section QA
    Testing        :         des4, after des3, 7d`
    }
};

const templateOptions = {
    plantuml: [
        { value: 'sequence', label: 'Sequence Diagram' },
        { value: 'usecase', label: 'Use Case' },
        { value: 'class', label: 'Class Diagram' },
        { value: 'activity', label: 'Activity Diagram' },
        { value: 'state', label: 'State Diagram' },
        { value: 'component', label: 'Component Diagram' },
        { value: 'er', label: 'ER Diagram' },
        { value: 'mindmap', label: 'Mind Map' },
        { value: 'gantt', label: 'Gantt Chart' }
    ],
    mermaid: [
        { value: 'sequence', label: 'Sequence Diagram' },
        { value: 'flowchart', label: 'Flowchart' },
        { value: 'class', label: 'Class Diagram' },
        { value: 'gantt', label: 'Gantt Chart' }
    ]
};

function getDiagramType() {
    const type = diagramTypeSelect?.value || DEFAULT_DIAGRAM_TYPE;
    return DIAGRAM_TYPES.includes(type) ? type : DEFAULT_DIAGRAM_TYPE;
}

function setDiagramType(type) {
    const normalized = DIAGRAM_TYPES.includes(type) ? type : DEFAULT_DIAGRAM_TYPE;
    if (diagramTypeSelect) {
        diagramTypeSelect.value = normalized;
    }
    populateTemplateSelect(normalized);
    populateFormatSelect(normalized);
}

function populateTemplateSelect(type = getDiagramType()) {
    if (!templateSelect) return;
    const options = templateOptions[type] || [];
    templateSelect.innerHTML = '<option value="">Templates...</option>';
    options.forEach(({ value, label }) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        templateSelect.appendChild(option);
    });
}

const formatOptions = {
    plantuml: [
        { value: 'svg', label: 'SVG' },
        { value: 'png', label: 'PNG' }
    ],
    mermaid: [
        { value: 'svg', label: 'SVG' },
        { value: 'text-unicode', label: 'Text (Unicode)' },
        { value: 'text-ascii', label: 'Text (ASCII)' }
    ]
};

function populateFormatSelect(type = getDiagramType()) {
    if (!formatSelect) return;
    const currentValue = formatSelect.value;
    const options = formatOptions[type] || formatOptions.plantuml;
    formatSelect.innerHTML = '';
    options.forEach(({ value, label }) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        formatSelect.appendChild(option);
    });
    // Preserve selection if valid for new type, otherwise default to first
    const validValues = options.map(o => o.value);
    if (validValues.includes(currentValue)) {
        formatSelect.value = currentValue;
    } else {
        formatSelect.value = options[0]?.value || 'svg';
    }
}

function buildDiagramPayload(content, type = getDiagramType()) {
    const normalized = DIAGRAM_TYPES.includes(type) ? type : DEFAULT_DIAGRAM_TYPE;
    return `<udiagram type="${normalized}"></diagram>\n${content}`;
}

function parseDiagramContent(raw = '') {
    if (!raw) {
        return { type: DEFAULT_DIAGRAM_TYPE, content: '' };
    }
    const match = raw.match(DIAGRAM_HEADER_REGEX);
    if (match) {
        const [, type] = match;
        let body = raw.slice(match[0].length);
        if (body.startsWith('\r\n')) {
            body = body.slice(2);
        } else if (body.startsWith('\n')) {
            body = body.slice(1);
        }
        return { type: type.toLowerCase(), content: body };
    }
    return { type: DEFAULT_DIAGRAM_TYPE, content: raw };
}

// ============ Diagram Storage (CRUD Storage) ============
const EMPTY_DIAGRAM_MESSAGE = 'No diagram loaded';

function escapeHtml(value = '') {
    return value.replace(/[&<>"']/g, (char) => {
        const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return map[char] || char;
    });
}

function summarizeDiagram() {
    const lines = umlInput.value.split('\n').map(line => line.trim()).filter(Boolean);
    if (!lines.length) return 'Empty diagram';
    const summary = lines[0];
    return summary.length > 80 ? summary.slice(0, 77) + '...' : summary;
}

function updateDiagramInfoDisplay() {
    if (!diagramInfo) return;
    if (!currentDiagram) {
        diagramInfo.textContent = EMPTY_DIAGRAM_MESSAGE;
        return;
    }
    const timestamp = currentDiagram.updatedAt
        ? new Date(currentDiagram.updatedAt).toLocaleString()
        : 'unsaved';
    const name = currentDiagram.name || 'Untitled Diagram';
    diagramInfo.textContent = `Current: ${name} (${timestamp})`;
}

async function updateStorageCount() {
    if (!storageCount) return;
    try {
        const count = await diagramStorage.count();
        storageCount.textContent = `${count} saved diagram${count === 1 ? '' : 's'}`;
    } catch (error) {
        console.error('Failed to read storage count', error);
        storageCount.textContent = 'Storage unavailable';
    }
}

function getDiagramData(nameOverride) {
    const name = nameOverride?.trim() || currentDiagram?.name || 'Untitled Diagram';
    const type = getDiagramType();
    return {
        id: currentDiagram?.id || Date.now(),
        name,
        description: summarizeDiagram(),
        template: buildDiagramPayload(umlInput.value, type),
        format: formatSelect.value,
        diagramType: type
    };
}

function loadDiagram(item) {
    currentDiagram = item;
    if (item.template !== undefined) {
        const { type, content } = parseDiagramContent(item.template);
        setDiagramType(type);
        umlInput.value = content;
        updateLineNumbers();
        renderDiagram();
    }
    if (item.format) {
        formatSelect.value = item.format;
    }
    updateDiagramInfoDisplay();
}

async function openStorageLoadModal() {
    const loadModal = new LoadModal({
        storage: diagramStorage,
        itemsPerPage: 7,
        formatItem: (item) => {
            const name = escapeHtml(item.name || 'Untitled diagram');
            const description = escapeHtml(item.description || 'No description');
            const updated = escapeHtml(item.updatedAt ? new Date(item.updatedAt).toLocaleString() : 'Never saved');
            const parsed = parseDiagramContent(item.template || '');
            const typeLabel = parsed.type === 'mermaid' ? 'Mermaid' : 'PlantUML';
            return `
                <div style="display:flex;flex-direction:column;gap:4px;">
                    <div style="font-weight:600;color:#111827;">${name}</div>
                    <div style="font-size:11px;color:#475569;text-transform:uppercase;letter-spacing:0.08em;">${typeLabel}</div>
                    <div style="font-size:12px;color:#4b5563;">${description}</div>
                    <div style="font-size:11px;color:#9ca3af;">Updated: ${updated}</div>
                </div>
            `;
        },
        onItemSelect: (item) => {
            loadDiagram(item);
            const displayName = item.name || 'diagram';
            setStatus('', `Loaded "${displayName}"`);
            showToast(`Loaded "${displayName}"`, 'success');
        }
    });

    await loadModal.open();
}

async function importDiagramFromDisk() {
    const data = await loadFromFile();
    if (!data) {
        setStatus('', 'Import cancelled');
        return;
    }
    loadDiagram({
        ...data,
        id: data.id || Date.now()
    });
    setStatus('', 'Loaded diagram from disk');
    showToast('Loaded diagram from disk', 'success');
}

async function saveDiagramToLibrary() {
    if (!umlInput.value.trim()) {
        setStatus('error', 'Nothing to save');
        showToast('Enter code before saving', 'error');
        return;
    }

    const promptModal = new PromptModal({
        title: 'Save Diagram',
        message: 'Enter a name for this diagram:',
        placeholder: 'Diagram name',
        defaultValue: currentDiagram?.name || summarizeDiagram()
    });

    const name = await promptModal.prompt();
    promptModal.destroy();

    if (!name) {
        setStatus('', 'Save cancelled');
        return;
    }

    const diagramData = getDiagramData(name);

    try {
        let saved;
        if (currentDiagram) {
            saved = await diagramStorage.update(currentDiagram.id, diagramData);
            if (!saved) {
                saved = await diagramStorage.create(diagramData);
            }
        } else {
            saved = await diagramStorage.create(diagramData);
        }

        currentDiagram = saved || diagramData;
        updateDiagramInfoDisplay();
        await updateStorageCount();
        setStatus('', `Saved "${diagramData.name}"`);
        showToast(`Saved "${diagramData.name}"`, 'success');
    } catch (error) {
        console.error(error);
        setStatus('error', 'Error saving diagram');
        showToast('Error saving diagram', 'error');
    }
}

async function exportDiagramToFileAction() {
    if (!umlInput.value.trim()) {
        setStatus('error', 'Nothing to export');
        showToast('Nothing to export', 'error');
        return;
    }

    const promptModal = new PromptModal({
        title: 'Export Diagram',
        message: 'Enter file name:',
        placeholder: 'diagram',
        defaultValue: currentDiagram?.name || 'diagram'
    });

    const filename = await promptModal.prompt();
    promptModal.destroy();

    if (!filename) {
        setStatus('', 'Export cancelled');
        return;
    }

    exportToFile(getDiagramData(filename), filename);
    setStatus('', `Exported "${filename}.json"`);
    showToast(`Exported ${filename}.json`, 'success');
}

function resetDiagram() {
    currentDiagram = null;
    umlInput.value = '';
    updateLineNumbers();
    saveEditorState();
    preview.innerHTML = `
        <div class="placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
            </svg>
            <div>Enter diagram code to render</div>
        </div>`;
    updateDiagramInfoDisplay();
    setStatus('', 'New diagram');
    renderDiagram();
}

function openCreditsModal() {
    const modal = new Modal({
        title: 'Credits',
        width: '500px',
    });

    const content = `
        <div style="color: #e6edf3; font-size: 14px; line-height: 1.6;">
            <p style="margin: 0 0 20px 0; color: #8b949e;">
                UDiagram is built with the following open source libraries:
            </p>

            <div style="margin-bottom: 16px; padding: 12px; background: #161b22; border-radius: 6px; border: 1px solid #30363d;">
                <div style="font-weight: 600; margin-bottom: 4px;">
                    <a href="https://github.com/mermaid-js/mermaid" target="_blank" rel="noopener" style="color: #58a6ff; text-decoration: none;">Mermaid</a>
                </div>
                <div style="color: #8b949e; font-size: 13px;">
                    JavaScript based diagramming and charting tool for dynamic diagrams.
                </div>
            </div>

            <div style="margin-bottom: 16px; padding: 12px; background: #161b22; border-radius: 6px; border: 1px solid #30363d;">
                <div style="font-weight: 600; margin-bottom: 4px;">
                    <a href="https://github.com/nicokosi/beautiful-mermaid" target="_blank" rel="noopener" style="color: #58a6ff; text-decoration: none;">beautiful-mermaid</a>
                </div>
                <div style="color: #8b949e; font-size: 13px;">
                    Renders Mermaid diagrams as SVG or ASCII/Unicode text art.
                    <br>
                    <span style="color: #7d8590;">Based on <a href="https://github.com/AlexanderGrooff/mermaid-ascii" target="_blank" rel="noopener" style="color: #58a6ff; text-decoration: none;">mermaid-ascii</a> by Alexander Grooff.</span>
                </div>
            </div>

            <div style="margin-bottom: 16px; padding: 12px; background: #161b22; border-radius: 6px; border: 1px solid #30363d;">
                <div style="font-weight: 600; margin-bottom: 4px;">
                    <a href="https://plantuml.com" target="_blank" rel="noopener" style="color: #58a6ff; text-decoration: none;">PlantUML</a>
                </div>
                <div style="color: #8b949e; font-size: 13px;">
                    Open-source tool for creating UML diagrams from plain text descriptions.
                </div>
            </div>

            <p style="margin: 20px 0 0 0; color: #7d8590; font-size: 12px; text-align: center;">
                Thank you to all the open source contributors!
            </p>
        </div>
    `;

    modal.setContent(content);

    // Style the modal for dark theme
    modal.element.style.background = '#0d1117';
    modal.element.style.border = '1px solid #30363d';
    const header = modal.element.querySelector('.modal-header');
    if (header) {
        header.style.background = '#161b22';
        header.style.borderBottom = '1px solid #30363d';
    }
    const title = modal.element.querySelector('.modal-title');
    if (title) {
        title.style.color = '#e6edf3';
    }
    const closeBtn = modal.element.querySelector('.modal-close');
    if (closeBtn) {
        closeBtn.style.color = '#8b949e';
    }

    modal.open();
}

function createFileMenu() {
    if (!fileMenuContainer) return;

    const menu = new FileMenu({
        'File': [
            {
                label: 'Load from Library',
                enabled: true,
                onClick: openStorageLoadModal
            },
            {
                label: 'Load from Disk',
                enabled: true,
                onClick: importDiagramFromDisk
            },
            { divider: true },
            {
                label: 'Save Diagram',
                enabled: true,
                onClick: saveDiagramToLibrary
            },
            {
                label: 'Export to File',
                enabled: true,
                onClick: exportDiagramToFileAction
            },
            { divider: true },
            {
                label: 'New Diagram',
                enabled: true,
                onClick: resetDiagram
            }
        ],
        'Help': [
            {
                label: 'About Storage',
                enabled: true,
                onClick: () => {
                    alert('CRUD Storage keeps your diagrams in browser localStorage.\nUse the File menu to load, save or export diagrams.');
                }
            },
            {
                label: 'Credits',
                enabled: true,
                onClick: openCreditsModal
            }
        ]
    });

    menu.appendTo(fileMenuContainer);
}

// ============ Line Numbers ============
function updateLineNumbers() {
    const lines = umlInput.value.split('\n').length;
    lineNumbers.innerHTML = Array.from({ length: lines }, (_, i) => i + 1).join('<br>');
}

// ============ Rendering ============
function setStatus(status, text) {
    statusDot.className = 'status-dot' + (status ? ' ' + status : '');
    statusText.textContent = text;
}

const diagramRenderers = {
    plantuml: {
        async render(content) {
            setStatus('loading', 'Rendering...');
            preview.innerHTML = `
                <div class="loading">
                    <div class="spinner"></div>
                    <div>Generating diagram...</div>
                </div>`;
            const format = formatSelect.value;
            const encoded = plantumlEncode(content);
            currentUrl = `https://www.plantuml.com/plantuml/${format}/${encoded}`;
            lastMermaidSVG = '';
            try {
                if (format === 'svg') {
                    const res = await fetch(currentUrl);
                    if (!res.ok) throw new Error('Server error');
                    const svg = await res.text();
                    preview.innerHTML = svg;
                    applyZoom();
                    setStatus('', 'Ready');
                } else {
                    const img = document.createElement('img');
                    img.onload = () => {
                        preview.innerHTML = '';
                        preview.appendChild(img);
                        applyZoom();
                        setStatus('', 'Ready');
                    };
                    img.onerror = () => {
                        throw new Error('Failed to load image');
                    };
                    img.src = currentUrl;
                }
            } catch (error) {
                preview.innerHTML = `
                    <div class="error">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <div>Failed to render diagram</div>
                        <div style="font-size: 12px; opacity: 0.8;">Check your PlantUML syntax</div>
                    </div>`;
                setStatus('error', 'Error');
            }
        }
    },
    mermaid: {
        async render(content) {
            setStatus('loading', 'Rendering...');
            preview.innerHTML = `
                <div class="loading">
                    <div class="spinner"></div>
                    <div>Generating diagram...</div>
                </div>`;
            currentUrl = '';
            lastMermaidSVG = '';
            lastMermaidText = '';

            const format = formatSelect.value;

            try {
                // Text output using beautiful-mermaid ASCII renderer
                if (format === 'text-unicode' || format === 'text-ascii') {
                    const useAscii = format === 'text-ascii';
                    const text = renderMermaidAscii(content, { useAscii });
                    lastMermaidText = text;
                    preview.innerHTML = `<pre class="ascii-preview">${escapeHtml(text)}</pre>`;
                    setStatus('', 'Ready');
                    return;
                }

                // SVG output using standard mermaid library
                const renderId = `mermaid-diagram-${Date.now()}-${mermaidRenderId++}`;
                const { svg } = await mermaid.render(renderId, content);
                preview.innerHTML = svg;
                lastMermaidSVG = svg;
                applyZoom();
                setStatus('', 'Ready');
            } catch (error) {
                console.error('Mermaid render error', error);
                preview.innerHTML = `
                    <div class="error">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <div>Failed to render diagram</div>
                        <div style="font-size: 12px; opacity: 0.8;">Check your Mermaid syntax</div>
                    </div>`;
                setStatus('error', 'Error');
            }
        }
    }
};

async function renderDiagram() {
    const source = umlInput.value.trim();
    if (!source) {
        currentUrl = '';
        lastMermaidSVG = '';
        preview.innerHTML = `
            <div class="placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="M21 15l-5-5L5 21"/>
                </svg>
                <div>Enter diagram code to render</div>
            </div>`;
        setStatus('', 'Ready');
        saveEditorState();
        return;
    }
    const type = getDiagramType();
    const renderer = diagramRenderers[type] || diagramRenderers[DEFAULT_DIAGRAM_TYPE];
    await renderer.render(source);
    saveEditorState();
}

function debounceRender() {
    clearTimeout(renderTimeout);
    renderTimeout = setTimeout(renderDiagram, 600);
}

// ============ Zoom ============
function applyZoom() {
    const content = preview.querySelector('svg, img');
    if (content) {
        content.style.transform = `scale(${currentZoom / 100})`;
        content.style.transformOrigin = 'center';
    }
    zoomLevel.textContent = currentZoom + '%';
}

document.getElementById('zoomIn').addEventListener('click', () => {
    currentZoom = Math.min(currentZoom + 25, 300);
    applyZoom();
});

document.getElementById('zoomOut').addEventListener('click', () => {
    currentZoom = Math.max(currentZoom - 25, 25);
    applyZoom();
});

document.getElementById('zoomReset').addEventListener('click', () => {
    currentZoom = 100;
    applyZoom();
});

// ============ Fullscreen ============
document.getElementById('fullscreenBtn').addEventListener('click', toggleFullscreen);

function toggleFullscreen() {
    isFullscreen = !isFullscreen;
    document.body.classList.toggle('fullscreen', isFullscreen);
    
    if (isFullscreen) {
        const exitBtn = document.createElement('button');
        exitBtn.className = 'btn fullscreen-exit';
        exitBtn.innerHTML = `
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 14h6v6m10-10h-6V4m0 6l7-7M3 21l7-7"/>
            </svg>
            Exit`;
        exitBtn.onclick = toggleFullscreen;
        document.body.appendChild(exitBtn);
    } else {
        document.querySelector('.fullscreen-exit')?.remove();
    }
}

// ============ Resizer ============
let isResizing = false;

resizer.addEventListener('mousedown', (e) => {
    isResizing = true;
    resizer.classList.add('active');
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResize);
});

function handleResize(e) {
    if (!isResizing) return;
    const containerWidth = document.querySelector('.container').offsetWidth;
    const newWidth = (e.clientX / containerWidth) * 100;
    if (newWidth > 20 && newWidth < 80) {
        editorPanel.style.width = newWidth + '%';
    }
}

function stopResize() {
    isResizing = false;
    resizer.classList.remove('active');
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', stopResize);
}

// ============ Share & Download ============
document.getElementById('shareBtn').addEventListener('click', async () => {
    if (getDiagramType() !== 'plantuml') {
        showToast('Sharing is only available for PlantUML diagrams right now.', 'error');
        return;
    }
    const encoded = plantumlEncode(umlInput.value);
    const shareUrl = `https://www.plantuml.com/plantuml/uml/${encoded}`;
    
    try {
        await navigator.clipboard.writeText(shareUrl);
        showToast('Link copied to clipboard!', 'success');
    } catch {
        showToast('Failed to copy link', 'error');
    }
});

document.getElementById('downloadBtn').addEventListener('click', async () => {
    const type = getDiagramType();
    const format = formatSelect.value;
    if (type === 'plantuml') {
        if (!currentUrl) {
            showToast('Nothing to download', 'error');
            return;
        }
        try {
            const res = await fetch(currentUrl);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `diagram.${format}`;
            a.click();
            URL.revokeObjectURL(url);
            showToast('Download started!', 'success');
        } catch {
            showToast('Download failed', 'error');
        }
        return;
    }

    // Mermaid text formats
    if (format === 'text-unicode' || format === 'text-ascii') {
        if (!lastMermaidText) {
            showToast('Render the diagram before downloading.', 'error');
            return;
        }
        try {
            const blob = new Blob([lastMermaidText], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'diagram.txt';
            a.click();
            URL.revokeObjectURL(url);
            showToast('Text file downloaded!', 'success');
        } catch {
            showToast('Download failed', 'error');
        }
        return;
    }

    // Mermaid SVG
    if (!lastMermaidSVG) {
        showToast('Render the diagram before downloading.', 'error');
        return;
    }
    try {
        const blob = new Blob([lastMermaidSVG], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'diagram.svg';
        a.click();
        URL.revokeObjectURL(url);
        showToast('SVG downloaded!', 'success');
    } catch {
        showToast('Download failed', 'error');
    }
});

// ============ Templates ============
templateSelect.addEventListener('change', () => {
    const presets = templatePresets[getDiagramType()] || {};
    const template = presets[templateSelect.value];
    if (template) {
        umlInput.value = template;
        updateLineNumbers();
        renderDiagram();
    }
    templateSelect.value = '';
});

// ============ Toast Notifications ============
function showToast(message, type = '') {
    const toast = document.createElement('div');
    toast.className = 'toast ' + type;
    toast.innerHTML = `
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
            ${type === 'success' 
                ? '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>'
                : '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>'}
        </svg>
        <span>${message}</span>`;
    document.getElementById('toastContainer').appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
}

// ============ Local Storage ============
function saveEditorState() {
    localStorage.setItem('plantuml-code', buildDiagramPayload(umlInput.value));
    localStorage.setItem('plantuml-format', formatSelect.value);
    localStorage.setItem('udiagram-type', getDiagramType());
}

function loadEditorState() {
    const saved = localStorage.getItem('plantuml-code');
    const format = localStorage.getItem('plantuml-format');
    const storedType = localStorage.getItem('udiagram-type');
    if (saved) {
        const { type, content } = parseDiagramContent(saved);
        setDiagramType(storedType || type);
        umlInput.value = content;
    } else if (storedType) {
        setDiagramType(storedType);
    } else {
        setDiagramType(DEFAULT_DIAGRAM_TYPE);
    }
    if (format) formatSelect.value = format;
}

// ============ Keyboard Shortcuts ============
document.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        renderDiagram();
    }
    if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        document.getElementById('downloadBtn').click();
    }
    if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
    }
    if (e.key === 'Escape' && isFullscreen) {
        toggleFullscreen();
    }
});

// ============ Event Listeners ============
umlInput.addEventListener('input', () => {
    updateLineNumbers();
    debounceRender();
});

umlInput.addEventListener('scroll', () => {
    lineNumbers.scrollTop = umlInput.scrollTop;
});

formatSelect.addEventListener('change', renderDiagram);
document.getElementById('renderBtn').addEventListener('click', renderDiagram);
if (diagramTypeSelect) {
    diagramTypeSelect.addEventListener('change', () => {
        populateTemplateSelect();
        populateFormatSelect();
        renderDiagram();
    });
}

// ============ Initialize ============
loadEditorState();
updateLineNumbers();
populateTemplateSelect();
populateFormatSelect();
createFileMenu();
updateDiagramInfoDisplay();
updateStorageCount();
renderDiagram();
